import React, { useState, useEffect } from 'react';
import { Users, Calendar, Video, MessageSquare, Heart, UserPlus, Activity, Building, BookOpen, Award } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import AdminGlobalLayout from '../../components/AdminGlobalLayout';
import AdminDashboardCard from '../../components/AdminDashboardCard';
import AdminDashboardChart from '../../components/AdminDashboardChart';

interface Stats {
  totalUsers: number;
  totalEvents: number;
  totalLiveStreams: number;
  totalMessages: number;
  totalPrayerRequests: number;
  totalSmallGroups: number;
  totalMinistries: number;
  totalIgrejas: number;
  totalCursos: number;
  totalTestimonials: number;
  userGrowth: number;
  eventGrowth: number;
  prayerGrowth: number;
}

interface IgrejaStats {
  id: string;
  nome: string;
  cidade: string;
  estado: string;
  total_usuarios: number;
}

export default function AdminGlobalDashboard() {
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    totalEvents: 0,
    totalLiveStreams: 0,
    totalMessages: 0,
    totalPrayerRequests: 0,
    totalSmallGroups: 0,
    totalMinistries: 0,
    totalIgrejas: 0,
    totalCursos: 0,
    totalTestimonials: 0,
    userGrowth: 0,
    eventGrowth: 0,
    prayerGrowth: 0
  });
  const [loading, setLoading] = useState(true);
  const [activityData, setActivityData] = useState<any[]>([]);
  const [usersByChurch, setUsersByChurch] = useState<IgrejaStats[]>([]);
  const [usersByMonthLabels, setUsersByMonthLabels] = useState<string[]>([]);
  const [usersByMonthData, setUsersByMonthData] = useState<any[]>([]);
  const [prayersByMonthLabels, setPrayersByMonthLabels] = useState<string[]>([]);
  const [prayersByMonthData, setPrayersByMonthData] = useState<any[]>([]);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);

  useEffect(() => {
    fetchStats();
    fetchActivityData();
    fetchUsersByChurch();
    fetchUsersByMonth();
    fetchPrayersByMonth();
    fetchRecentActivities();
  }, []);

  async function fetchStats() {
    try {
      setLoading(true);
      
      // Fetch counts from various tables
      const [
        { count: usersCount },
        { count: eventsCount },
        { count: liveStreamsCount },
        { count: messagesCount },
        { count: prayerRequestsCount },
        { count: smallGroupsCount },
        { count: ministriesCount },
        { count: igrejasCount },
        { count: cursosCount },
        { count: testimonialsCount }
      ] = await Promise.all([
        supabase.from('users').select('*', { count: 'exact', head: true }),
        supabase.from('events').select('*', { count: 'exact', head: true }),
        supabase.from('live_streams').select('*', { count: 'exact', head: true }),
        supabase.from('messages').select('*', { count: 'exact', head: true }),
        supabase.from('prayer_requests').select('*', { count: 'exact', head: true }),
        supabase.from('small_groups').select('*', { count: 'exact', head: true }),
        supabase.from('ministries').select('*', { count: 'exact', head: true }),
        supabase.from('igrejas').select('*', { count: 'exact', head: true }),
        supabase.from('courses').select('*', { count: 'exact', head: true }),
        supabase.from('testimonials').select('*', { count: 'exact', head: true })
      ]);

      // Calculate growth (example - you would need to implement the real logic)
      const userGrowth = 15; // Growth of 15% compared to previous month
      const eventGrowth = 8;
      const prayerGrowth = 12;

      setStats({
        totalUsers: usersCount || 0,
        totalEvents: eventsCount || 0,
        totalLiveStreams: liveStreamsCount || 0,
        totalMessages: messagesCount || 0,
        totalPrayerRequests: prayerRequestsCount || 0,
        totalSmallGroups: smallGroupsCount || 0,
        totalMinistries: ministriesCount || 0,
        totalIgrejas: igrejasCount || 0,
        totalCursos: cursosCount || 0,
        totalTestimonials: testimonialsCount || 0,
        userGrowth,
        eventGrowth,
        prayerGrowth
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchActivityData() {
    // Example activity data - replace with real data from database
    const data = [
      { name: 'Jan', users: 400 },
      { name: 'Fev', users: 300 },
      { name: 'Mar', users: 600 },
      { name: 'Abr', users: 800 },
      { name: 'Mai', users: 1000 },
      { name: 'Jun', users: 1200 },
    ];
    setActivityData(data);
  }

  async function fetchUsersByChurch() {
    try {
      // In a real implementation, you would fetch this data from the database
      // For now, we'll use mock data
      const mockData: IgrejaStats[] = [
        { id: '1', nome: 'Sal da Terra Brasília', cidade: 'Brasília', estado: 'DF', total_usuarios: 120 },
        { id: '2', nome: 'Sal da Terra São Paulo', cidade: 'São Paulo', estado: 'SP', total_usuarios: 85 },
        { id: '3', nome: 'Sal da Terra Rio de Janeiro', cidade: 'Rio de Janeiro', estado: 'RJ', total_usuarios: 67 },
        { id: '4', nome: 'Sal da Terra Belo Horizonte', cidade: 'Belo Horizonte', estado: 'MG', total_usuarios: 54 },
        { id: '5', nome: 'Sal da Terra Goiânia', cidade: 'Goiânia', estado: 'GO', total_usuarios: 42 },
      ];
      
      setUsersByChurch(mockData);
    } catch (error) {
      console.error('Error fetching users by church:', error);
    }
  }

  async function fetchUsersByMonth() {
    // Mock data for users by month
    const labels = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'];
    const data = [
      { month: 'Jan', users: 50 },
      { month: 'Fev', users: 75 },
      { month: 'Mar', users: 90 },
      { month: 'Abr', users: 120 },
      { month: 'Mai', users: 150 },
      { month: 'Jun', users: 180 },
    ];
    
    setUsersByMonthLabels(labels);
    setUsersByMonthData(data);
  }

  async function fetchPrayersByMonth() {
    // Mock data for prayers by month
    const labels = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'];
    const data = [
      { month: 'Jan', prayers: 30 },
      { month: 'Fev', prayers: 45 },
      { month: 'Mar', prayers: 60 },
      { month: 'Abr', prayers: 75 },
      { month: 'Mai', prayers: 90 },
      { month: 'Jun', prayers: 105 },
    ];
    
    setPrayersByMonthLabels(labels);
    setPrayersByMonthData(data);
  }

  async function fetchRecentActivities() {
    // Mock data for recent activities
    const activities = [
      { 
        id: 1, 
        type: 'user_created', 
        description: 'Novo usuário registrado: Maria Silva', 
        timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
        icon: UserPlus,
        color: 'bg-blue-100 text-blue-600'
      },
      { 
        id: 2, 
        type: 'event_created', 
        description: 'Novo evento criado: Retiro de Jovens', 
        timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
        icon: Calendar,
        color: 'bg-green-100 text-green-600'
      },
      { 
        id: 3, 
        type: 'prayer_request', 
        description: 'Novo pedido de oração recebido', 
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        icon: Heart,
        color: 'bg-red-100 text-red-600'
      },
      { 
        id: 4, 
        type: 'course_completed', 
        description: 'João Oliveira concluiu o curso "Fundamentos da Fé"', 
        timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
        icon: Award,
        color: 'bg-purple-100 text-purple-600'
      },
      { 
        id: 5, 
        type: 'church_added', 
        description: 'Nova igreja adicionada: Sal da Terra Fortaleza', 
        timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        icon: Building,
        color: 'bg-amber-100 text-amber-600'
      }
    ];
    
    setRecentActivities(activities);
  }

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMins < 60) {
      return `Há ${diffMins} ${diffMins === 1 ? 'minuto' : 'minutos'}`;
    }
    
    const diffHours = Math.round(diffMins / 60);
    if (diffHours < 24) {
      return `Há ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
    }
    
    const diffDays = Math.round(diffHours / 24);
    return `Há ${diffDays} ${diffDays === 1 ? 'dia' : 'dias'}`;
  };

  const cards = [
    {
      title: 'Igrejas',
      value: stats.totalIgrejas,
      icon: Building,
      color: 'bg-amber-100 text-amber-600',
    },
    {
      title: 'Usuários',
      value: stats.totalUsers,
      icon: Users,
      color: 'bg-blue-100 text-blue-600',
      change: { value: stats.userGrowth, type: 'increase' as const }
    },
    {
      title: 'Eventos',
      value: stats.totalEvents,
      icon: Calendar,
      color: 'bg-green-100 text-green-600',
      change: { value: stats.eventGrowth, type: 'increase' as const }
    },
    {
      title: 'Transmissões',
      value: stats.totalLiveStreams,
      icon: Video,
      color: 'bg-red-100 text-red-600'
    },
    {
      title: 'Mensagens',
      value: stats.totalMessages,
      icon: MessageSquare,
      color: 'bg-purple-100 text-purple-600'
    },
    {
      title: 'Pedidos de Oração',
      value: stats.totalPrayerRequests,
      icon: Heart,
      color: 'bg-pink-100 text-pink-600',
      change: { value: stats.prayerGrowth, type: 'increase' as const }
    },
    {
      title: 'Pequenos Grupos',
      value: stats.totalSmallGroups,
      icon: Users,
      color: 'bg-indigo-100 text-indigo-600'
    },
    {
      title: 'Cursos',
      value: stats.totalCursos,
      icon: BookOpen,
      color: 'bg-teal-100 text-teal-600'
    }
  ];

  return (
    <AdminGlobalLayout title="Dashboard Global">
      {/* Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => (
          <AdminDashboardCard
            key={card.title}
            title={card.title}
            value={card.value}
            icon={card.icon}
            color={card.color}
            loading={loading}
            change={card.change}
          />
        ))}
      </div>

      {/* Charts */}
      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <AdminDashboardChart 
          title="Crescimento de Usuários" 
          data={usersByMonthData} 
          labels={usersByMonthLabels}
          dataKey="users" 
          type="line"
          loading={loading}
        />

        <AdminDashboardChart 
          title="Pedidos de Oração" 
          data={prayersByMonthData} 
          labels={prayersByMonthLabels}
          dataKey="prayers" 
          type="bar"
          color="#EC4899"
          loading={loading}
        />
      </div>

      {/* Users by Church */}
      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <div className="bg-white p-6 rounded-xl shadow-elevation-1">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building className="h-5 w-5 text-brand-teal" />
            Usuários por Igreja
          </h2>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Igreja</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Localização</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Usuários</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {usersByChurch.map((igreja) => (
                    <tr key={igreja.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{igreja.nome}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{igreja.cidade}/{igreja.estado}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-brand-teal">{igreja.total_usuarios}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white p-6 rounded-xl shadow-elevation-1">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-brand-teal" />
            Atividade Recente
          </h2>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className={`p-2 rounded-lg ${activity.color}`}>
                    <activity.icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{activity.description}</p>
                    <p className="text-sm text-gray-500">{formatTimeAgo(activity.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminGlobalLayout>
  );
}